import { action, computed, observable, makeAutoObservable } from 'mobx';
import { TrpcClient } from 'src/AppState/TrpcClient';
import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { FooterContactUsElementsState, ContactOptionTypeZod } from 'src/main/cms/FooterManagement/FooterManagement';
import { FooterContactUsDataModelType } from 'src/api/config/footerManagement/footerContactUsElements/getContactUsElements';
import { timeout } from 'src_common/common/mobx-utils/timeout';
import { createEmailInputState, createLinkInputState, createPhoneInputState } from './FooterContactUsSingle.utils';

interface FormType {
    contact_value: string;
    label: string;
    is_active: boolean;
}

interface DataType {
    id: number;
    direction: 'up' | 'down';
}

export class FooterContactUsSingleElementState {
    @observable public errorMessageForList: string | null = null;

    @observable private contactUsElement: FooterContactUsDataModelType;
    @observable private contactUsState: FooterContactUsElementsState;

    @observable public contactValue: FormInputState<string, string>;
    @observable public contactLabel: FormInputState<string, string>;
    @observable public isActive: FormInputState<boolean, boolean>;

    @observable public initialContactValue: string;
    @observable public initialContactLabel: string;

    private readonly form: FormModel<FormType>;

    public constructor(
        contactUsElement: FooterContactUsDataModelType,
        contactUsState: FooterContactUsElementsState,
        private readonly trpcClient: TrpcClient
    ) {
        this.contactUsElement = contactUsElement;
        this.contactUsState = contactUsState;

        const defaultContactValue = this.contactUsElement.contact_value;
        try {
            const contactType = ContactOptionTypeZod.parse(this.contactUsElement.contact_type);

            switch (contactType) {
                case 'e-mail':
                    this.contactValue = createEmailInputState(defaultContactValue);
                    break;
                case 'phone-number':
                    this.contactValue = createPhoneInputState(defaultContactValue);
                    break;
                case 'twitter':
                case 'instagram':
                case 'facebook':
                    this.contactValue = createLinkInputState(defaultContactValue);
                    break;
            }
        } catch {
            this.contactValue = FormInputState.new(defaultContactValue);
        }

        this.isActive = FormInputState.new(this.contactUsElement.is_active);

        this.contactLabel = FormInputState.new(this.contactUsElement.label ?? '');

        this.initialContactValue = contactUsElement.contact_value;
        this.initialContactLabel = contactUsElement.label ?? '';

        this.form = FormModel.group({
            contact_value: this.contactValue,
            label: this.contactLabel,
            is_active: this.isActive,
        });
        makeAutoObservable(this);
    }

    @action private handleError = async (message: string): Promise<void> => {
        this.errorMessageForList = message;
        await timeout(5000);
        this.errorMessageForList = null;
    };

    @action public setIsActive = async (id: number): Promise<void> => {
        const item = this.contactUsState.contactUsElementsData.find((item) => item.id === id);

        if (item === undefined) {
            console.error('Cannot find the location with provided ID.');
        } else {
            const params = {
                data: {
                    ...item,
                    is_active: !item.is_active,
                },
                id: id,
            };

            this.isActive.setValue(!this.isActive.value);

            try {
                await this.trpcClient.client.footerContactElementsRouter.editContactElement.mutate(params);
                await this.contactUsState.contactUsElementsResource.refresh();
            } catch (error) {
                console.error(error);
            }
        }
    };

    @action public moveContactItemUp = async (id: number): Promise<void> => {
        const data: DataType = {
            id,
            direction: 'up',
        };
        try {
            const result = await this.trpcClient.client.footerContactElementsRouter.changeContactElementsOrder.mutate({
                data,
            });
            if (result.status === 200) {
                return this.contactUsState.contactUsElementsResource.refresh();
            } else {
                return this.handleError('Can not move item up.');
            }
        } catch (error) {
            console.error('Can not move item up.');
        }
    };

    @action public moveContactItemDown = async (id: number): Promise<void> => {
        const data: DataType = {
            id,
            direction: 'down',
        };
        try {
            const result = await this.trpcClient.client.footerContactElementsRouter.changeContactElementsOrder.mutate({
                data,
            });
            if (result.status === 200) {
                return this.contactUsState.contactUsElementsResource.refresh();
            } else {
                return this.handleError('Can not move item down.');
            }
        } catch (error) {
            console.error('Can not move item up.');
        }
    };

    @action public handleEdit = async (contactType: string): Promise<void> => {
        this.form.setAsVisited();
        const result = this.form.result;

        if (result.value.type === 'ok') {
            const contactUsDetailsData = {
                ...result.value.data,
                contact_type: contactType,
                display_order: this.contactUsElement.display_order,
            };

            const submitResult = await this.trpcClient.client.footerContactElementsRouter.editContactElement.mutate({
                data: contactUsDetailsData,
                id: this.contactUsElement.id,
            });

            if (submitResult.status === 200) {
                await this.contactUsState.contactUsElementsResource.refresh();
            } else {
                console.error('Error');
            }

            this.initialContactValue = this.contactValue.value;
            this.initialContactLabel = this.contactLabel.value;
        }
    };

    @action public handleDelete = async (id: number): Promise<void> => {
        const removeResult = await this.trpcClient.client.footerContactElementsRouter.deleteContactElement.mutate({
            id,
        });
        if (removeResult.status === 200) {
            await this.contactUsState.contactUsElementsResource.refresh();
        } else {
            // prettier-ignore
            console.error('The item couldn\'t be deleted.');
        }
    };

    @computed public get hasInputChanged(): boolean {
        const hasValueChanged = this.initialContactValue !== this.contactValue.value;
        const hasLabelChanged = this.initialContactLabel !== this.contactLabel.value;

        return hasValueChanged || hasLabelChanged;
    }
}
